/**
 * Afisajul de perete - `Monitor::wallboard($token)` din portal.
 *
 * NU TRECE PRIN `client.ts`, si asta e tot rostul fisierului. Drumul obisnuit
 * trimite cookie-ul `od_v4`, incearca o reinnoire de sesiune la 401 si, daca
 * nu reuseste, MUTA BROWSERUL la ecranul de intrare. Toate trei sunt gresite
 * aici:
 *
 *  - cookie-ul nu are ce cauta: cheia e adresa, si daca cel care a lipit-o pe
 *    televizor era logat cand a deschis-o, pagina n-are voie sa se poarte
 *    altfel decat se va purta maine dimineata, cand nu mai e nimeni logat.
 *    De-aia `credentials: 'omit'` - nu „same-origin", ci deloc.
 *  - o reinnoire de sesiune n-are ce reinnoi;
 *  - iar o plecare la ecranul de intrare in fata unui televizor inseamna un
 *    perete pe care scrie „Sign in" pana vine cineva cu o tastatura.
 *
 * Capetele sunt sub `/api/v2/public/`, singurul prefix din API care raspunde
 * fara autentificare (vezi `config/packages/security.yaml`).
 */

export interface WallboardTile {
  label: string
  value: number
  /** `ok` / `bad` / `warn` / `live` / `idle` / `plain` - culoarea starii. */
  tone: string
}

export interface WallboardRow {
  /** `Y-m-d H:i:s`, sau gol. Pe perete se arata doar `H:i`. */
  when: string
  what: string
  note: string
  tone: string
  state: string
}

export interface WallboardSeries {
  label: string
  tone: string
  /** Paisprezece valori, cate una pe zi - zilele goale sunt zerouri, nu lipsuri. */
  values: number[]
}

export interface WallboardChart {
  title: string
  labels: string[]
  series: WallboardSeries[]
}

export interface WallboardFigures {
  tiles: WallboardTile[]
  rows: WallboardRow[]
  rowsTitle: string
  chart: WallboardChart | null
}

export interface WallboardDisplayPage {
  name: string
  view: number
  viewLabel: string
  theme: string
  refreshSeconds: number
  figures: WallboardFigures
}

/**
 * De ce trei stari si nu doua.
 *
 * „N-a raspuns" si „nu mai e o tabla" trebuie sa se poarte DIFERIT pe un
 * perete, desi amandoua sunt esecuri:
 *
 *   `gone`   - serverul a spus limpede ca jetonul nu e bun (404). Tabla a fost
 *              retrasa, sau n-a existat. Cifrele de pe ecran nu mai au voie sa
 *              stea acolo: o tabla retrasa care isi tine ultimele cifre pe
 *              perete pana stinge cineva televizorul e o tabla care n-a fost
 *              retrasa.
 *   `down`   - n-a raspuns nimeni (retea cazuta, server repornit). Cifrele de
 *              dinainte RAMAN pe ecran si doar batatia inimii se face rosie.
 *              O pana de retea de zece secunde n-are voie sa albeasca un
 *              perete.
 */
type Failed = { kind: 'gone' } | { kind: 'down' }

/** Prima desenare: tabla intreaga, sau unul din cele doua esecuri. */
export type WallboardLoad = { kind: 'ok'; page: WallboardDisplayPage } | Failed

/** Improspatarea: numai cifrele, sau unul din cele doua esecuri. */
export type WallboardRefresh = { kind: 'figures'; figures: WallboardFigures } | Failed

const ROOT = '/api/v2/public/wallboard/'

async function ask(path: string, signal?: AbortSignal): Promise<unknown | 'gone' | 'down'> {
  let response: Response

  try {
    response = await fetch(path, {
      signal,
      /* Nici macar pe acelasi domeniu: vezi capul fisierului. */
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })
  } catch {
    return 'down'
  }

  /* Orice raspuns limpede „nu" - 404 de la noi, 410, 403 - e o tabla care nu
     mai e. Un 500 sau un 502 e serverul, nu jetonul, si atunci cifrele raman. */
  if (response.status === 404 || response.status === 410 || response.status === 403) return 'gone'
  if (!response.ok) return 'down'

  try {
    return (await response.json()) as unknown
  } catch {
    return 'down'
  }
}

/** Prima desenare: ce e tabla, si cifrele ei. */
export async function loadWallboard(token: string, signal?: AbortSignal): Promise<WallboardLoad> {
  const body = await ask(ROOT + encodeURIComponent(token), signal)

  if (body === 'gone') return { kind: 'gone' }
  if (body === 'down') return { kind: 'down' }

  return { kind: 'ok', page: body as WallboardDisplayPage }
}

/** Improspatarea: numai cifrele, fara sa se reincarce pagina. */
export async function refreshWallboard(token: string, signal?: AbortSignal): Promise<WallboardRefresh> {
  const body = await ask(`${ROOT}${encodeURIComponent(token)}/data`, signal)

  if (body === 'gone') return { kind: 'gone' }
  if (body === 'down') return { kind: 'down' }

  const figures = body as WallboardFigures & { status?: string }

  /* Corpul trebuie sa fie cifre, nu orice JSON: un intermediar care raspunde cu
     pagina lui de eroare ar sterge altfel tot peretele. */
  if (figures.status !== 'OK' || !Array.isArray(figures.tiles)) return { kind: 'down' }

  return { kind: 'figures', figures }
}
